/**
 * FO-349 (batch-40) — factuurdatum-rekenwerk voor EIGEN verkoopfacturen.
 *
 * Eén bron voor de drie schrijfpaden (definitief-modal, concept-PATCH,
 * correctie-route) én voor de backoffice-modal, zodat ze het niet oneens kunnen
 * worden over "welke dag is dit", "wat wordt de vervaldatum" of hoe een
 * waarschuwing luidt. De modal toont de vervaldatum en de verleden-/toekomst-
 * waarschuwing al live tijdens het kiezen; de API vult daar de waarschuwingen
 * bij aan die serverkennis vereisen (datum-inversie t.o.v. het vorige nummer).
 *
 * Twee dingen die hier bewust op één plek staan:
 *
 * 1. **NL-dag, niet server-dag.** De factuurdatum is een NL-kalenderdag; hij
 *    wordt opgeslagen als het UTC-instant van het NL-dagbegin. Om 00:30 NL is
 *    het in UTC nog de vorige dag - een naïeve UTC-dagafleiding zou de factuur
 *    dan een dag terugzetten.
 * 2. **Vervaldatum in kalenderdagen, niet in milliseconden.** `datum + 30 * 86400000`
 *    lijkt goed maar schuift een uur bij een DST-overgang, en dat is precies
 *    genoeg om op de vorige dag te landen: 1 okt (NL-dagbegin = 30 sep 22:00Z)
 *    + 30 dagen aan ms = 30 okt 22:00Z = 30 okt 23:00 NL, terwijl 1 okt + 30
 *    dagen = 31 oktober. Een betalingstermijn die een dag te kort is op een
 *    fiscaal document. Daarom kalender-optellen via `ymdPlusDagen`.
 *
 * Inkoopfacturen (Finqle-import) hebben hun eigen datumbron en komen hier niet
 * langs (aanname A1).
 */
import { amsterdamAllDayStartToUtc, utcToAmsterdamWallclock } from './amsterdam-tijd'
import { ymdPlusDagen } from './gespreksverslag-deadline'
import { isFactuurdatumYmd, type FactuurdatumWaarschuwing } from '@streep/types'

/** De NL-kalenderdag ('yyyy-MM-dd') waarop dit instant valt. */
export function nlDagVan(d: Date): string {
  return utcToAmsterdamWallclock(d).ymd
}

/** Vandaag als NL-kalenderdag. */
export function vandaagNlDag(now: Date = new Date()): string {
  return nlDagVan(now)
}

/**
 * Normaliseert de invoer naar een NL-kalenderdag.
 *
 * Accepteert beide vormen die het write-schema toelaat: een kale 'yyyy-MM-dd'
 * (wat het form stuurt) en een ISO-datetime (wat de read-DTO teruggeeft). De
 * ISO-variant wordt als instant gelezen en in NL-tijd naar zijn dag gebracht,
 * zodat een teruggekaatste read-DTO exact dezelfde dag oplevert - anders zou
 * een round-trip de factuur rond middernacht een dag verschuiven.
 *
 * @returns de NL-dag, of `null` als de waarde onparseerbaar is.
 */
export function factuurdatumNaarNlDag(waarde: string): string | null {
  const v = waarde.trim()
  if (isFactuurdatumYmd(v)) return v
  const t = Date.parse(v)
  if (Number.isNaN(t)) return null
  return nlDagVan(new Date(t))
}

/** UTC-instant van het NL-dagbegin van `ymd` - de opslagvorm van `invoiceDate`. */
export function nlDagbeginUtc(ymd: string): Date {
  return amsterdamAllDayStartToUtc(ymd)
}

/**
 * Vervaldatum = factuurdatum + betalingstermijn, geteld in NL-kalenderdagen en
 * opgeslagen als NL-dagbegin. Zie de DST-val in de module-toelichting hierboven.
 */
export function afgeleideVervaldatumUtc(invoiceYmd: string, termijnDagen: number): Date {
  return nlDagbeginUtc(ymdPlusDagen(invoiceYmd, termijnDagen))
}

/**
 * Volgt de opgeslagen vervaldatum nog automatisch de factuurdatum, of is hij
 * ooit handmatig gezet?
 *
 * FO-349 voegt geen kolom toe die dat markeert, dus we leiden het af uit de
 * data zelf: valt `dueDate` op de dag die uit `invoiceDate + termijn` volgt,
 * dan is hij afgeleid en mag hij meebewegen. Wijkt hij af, dan heeft iemand hem
 * bewust gezet en laten we hem staan (§3.5) met een waarschuwing.
 *
 * Vergelijkt op NL-dag, niet op instant: historische rijen hebben een
 * tijdcomponent (`now` + 30 dagen aan ms) terwijl nieuwe op het dagbegin staan.
 * Beide horen als "afgeleid" te gelden.
 */
export function vervaldatumVolgtAutomatisch(
  invoiceDate: Date | null,
  dueDate: Date | null,
  termijnDagen: number,
): boolean {
  // Nog geen vervaldatum, of geen factuurdatum om tegen af te zetten: niets te
  // behouden, laat 'm afgeleid worden.
  if (dueDate == null || invoiceDate == null) return true
  return nlDagVan(dueDate) === ymdPlusDagen(nlDagVan(invoiceDate), termijnDagen)
}

/** NL-weergave van een kalenderdag: 'yyyy-MM-dd' → 'dd-MM-yyyy'. */
export function formatNlDag(ymd: string): string {
  const [j, m, d] = ymd.split('-')
  return `${d}-${m}-${j}`
}

/** De NL-maandnaam van een kalenderdag ('2026-06-28' → 'juni'). */
function nlMaandNaam(ymd: string): string {
  const [j, m, d] = ymd.split('-').map((x) => parseInt(x, 10))
  return new Intl.DateTimeFormat('nl-NL', {
    timeZone: 'Europe/Amsterdam',
    month: 'long',
  }).format(new Date(Date.UTC(j, m - 1, d, 12)))
}

export interface FactuurdatumWaarschuwingInput {
  /** De gekozen factuurdatum als NL-kalenderdag. */
  gekozenYmd: string
  /** Vandaag als NL-kalenderdag. */
  vandaagYmd: string
  /** Het factuurnummer van deze factuur (voor de inversie-tekst). */
  invoiceNumber?: string | null
  /** Het laatst uitgegeven nummer van dezelfde BV, als dat er is. */
  vorigeFactuur?: { invoiceNumber: string; invoiceDate: Date | null } | null
  /** True als de vervaldatum handmatig gezet is en dus niet meebeweegt. */
  vervaldatumHandmatig?: boolean
}

/**
 * De informatieve waarschuwingen bij een gekozen factuurdatum (§7).
 *
 * Allemaal niet-blokkerend: STREEP kent geen periode-afsluiting in de app (A2),
 * dus antedateren mag - het moet alleen een bewuste keuze zijn. De UI toont ze
 * inline in de modal.
 */
export function bouwFactuurdatumWaarschuwingen(
  input: FactuurdatumWaarschuwingInput,
): FactuurdatumWaarschuwing[] {
  const warnings: FactuurdatumWaarschuwing[] = []
  const { gekozenYmd, vandaagYmd } = input

  // 'yyyy-MM-dd' is lexicografisch = chronologisch.
  if (gekozenYmd < vandaagYmd) {
    warnings.push({
      code: 'DATUM_IN_VERLEDEN',
      message: `Factuurdatum ligt vóór vandaag; de factuur valt in periode ${nlMaandNaam(gekozenYmd)}.`,
    })
  } else if (gekozenYmd > vandaagYmd) {
    warnings.push({
      code: 'DATUM_IN_TOEKOMST',
      message:
        `Factuurdatum ligt in de toekomst; de bewaking op openstaande facturen ` +
        `start pas vanaf ${formatNlDag(gekozenYmd)}.`,
    })
  }

  // §8.3 — nummers worden op uitgiftevolgorde toegekend en nooit herschikt.
  // Antedateren kan dus een datum-inversie geven t.o.v. het vorige nummer. Dat
  // is toegestaan, maar moet bewust zijn.
  const vorige = input.vorigeFactuur
  if (vorige?.invoiceDate && input.invoiceNumber) {
    const vorigeYmd = nlDagVan(vorige.invoiceDate)
    if (gekozenYmd < vorigeYmd) {
      warnings.push({
        code: 'DATUM_INVERSIE_VORIG_NUMMER',
        message:
          `Let op: factuurnummer ${input.invoiceNumber} krijgt een eerdere datum ` +
          `(${formatNlDag(gekozenYmd)}) dan ${vorige.invoiceNumber} ` +
          `(${formatNlDag(vorigeYmd)}).`,
      })
    }
  }

  if (input.vervaldatumHandmatig) {
    warnings.push({
      code: 'VERVALDATUM_HANDMATIG',
      message:
        'De vervaldatum is handmatig gezet en beweegt niet mee met de factuurdatum. ' +
        'Pas hem zo nodig zelf aan.',
    })
  }

  return warnings
}
